import * as APIModels from "../models";

export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export function Base64EncodeStrings(strings: string[]): string {
    let combined = "";
    strings.forEach((s) => {
        combined += s + "|";
    });

    // remove the last pipe
    combined = combined.substring(0, combined.length - 1);
    return btoa(combined);
}

class DetailedResponse<T> {
    Successful: boolean;
    ErrorMessage: string;
    Data: T;

    constructor(successful: boolean, errorMessage: string, data: T) {
        this.Successful = successful;
        this.ErrorMessage = errorMessage;
        this.Data = data;
    }

    static fromJson<T>(json: string): DetailedResponse<T> {
        return JSON.parse(json) as DetailedResponse<T>;
    }

    toJson(): string {
        return JSON.stringify(this);
    }
}

enum FrogLevel {
    Trace = 0,
    Debug = 1,
    Info = 2,
    Warn = 3,
    Error = 4,
    Fatal = 5,
}

class FrogLog {
    ApiCall: string;
    LogLevel: number;
    LogMessage: string;
    LogTime: Date;
    Source: string;
    PeekName: string;
    PeekValue: string;

    constructor(
        Source: string,
        ApiCall: string,
        LogLevel: FrogLevel,
        LogMessage: string
    ) {
        this.Source = Source;
        this.ApiCall = ApiCall;
        this.LogLevel = LogLevel;
        this.LogMessage = LogMessage;
        this.LogTime = new Date();
        this.PeekName = "";
        this.PeekValue = "";
    }

    PeekAssign(name: string, value: string) {
        this.PeekName = name;
        this.PeekValue = value;
    }

    PeekClear() {
        this.PeekName = "";
        this.PeekValue = "";
    }

    toString() {
        return `${this.Source}::${
            this.ApiCall
        }-${this.LogTime.toISOString()}\t${this.LogLevel}\t${
            this.LogMessage
        }\t${this.PeekName}->${this.PeekValue}`;
    }

    toJson() {
        return JSON.stringify(this);
    }

    static fromJson(json: string) {
        return JSON.parse(json) as FrogLog;
    }
}

class Frogger {
    private readonly _endpoint: string;
    private readonly _source: string;
    private readonly _apikey: string;

    constructor(endpoint: string, source: string, apikey: string) {
        this._endpoint = endpoint;
        this._source = source;
        this._apikey = apikey;
    }

    Encode() {
        return Buffer.from(this._apikey).toString("base64");
    }

    async FroggerTrace(
        apiCall: string,
        message: string,
        peekName: string,
        peekValue: string
    ) {
        const log = new FrogLog(
            this._source,
            apiCall,
            FrogLevel.Trace,
            message
        );
        log.PeekAssign(peekName, peekValue);

        // use fetch to post to the endpoint
        const httpResponse = await fetch(`${this._endpoint}/api/write`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${this.Encode()}`,
            },
            body: log.toJson(),
        });

        if (httpResponse.ok) {
            const json = await httpResponse.text();
            const response = JSON.parse(json) as DetailedResponse<Date>;
            if (response.Successful) {
                // Do something
            } else {
                throw new Error(response.ErrorMessage);
            }
        } else {
            throw new Error(httpResponse.statusText);
        }
    }

    async FroggerDebug(
        apiCall: string,
        message: string,
        peekName: string,
        peekValue: string
    ) {
        const log = new FrogLog(
            this._source,
            apiCall,
            FrogLevel.Debug,
            message
        );
        log.PeekAssign(peekName, peekValue);

        // use fetch to post to the _endpoint
        const httpResponse = await fetch(`${this._endpoint}/api/write`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${this.Encode()}`,
            },
            body: log.toJson(),
        });

        if (httpResponse.ok) {
            const json = await httpResponse.text();
            const response = JSON.parse(json) as DetailedResponse<Date>;
            if (response.Successful) {
                // Do something
                console.log(response.Data);
            } else {
                throw new Error(response.ErrorMessage);
            }
        } else {
            throw new Error(httpResponse.statusText);
        }
    }

    async FroggerInfo(
        apiCall: string,
        message: string,
        peekName: string,
        peekValue: string
    ) {
        const log = new FrogLog(this._source, apiCall, FrogLevel.Info, message);
        log.PeekAssign(peekName, peekValue);

        const httpResponse = await fetch(`${this._endpoint}/api/write`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${this.Encode()}`,
            },
            body: log.toJson(),
        });

        if (httpResponse.ok) {
            const json = await httpResponse.text();
            const response = JSON.parse(json) as DetailedResponse<Date>;
            if (response.Successful) {
                // Do something
            } else {
                throw new Error(response.ErrorMessage);
            }
        } else {
            throw new Error(httpResponse.statusText);
        }
    }
    // Add similar methods for FroggerWarn, FroggerError, and FroggerFatal
}
